import React, { useState } from "react";

import { isEqual, isNull } from "lodash/fp";

import Typography from "@mui/material/Typography";

import type { UpcomingEventShareDialogTab } from "@app/analytics/Calendar/types";
import { DesktopOnlyTableCell } from "@app/analytics/components/Table";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableRow from "@app/analytics/components/Table/TableRow";
import type { UserEventGroup } from "@app/analytics/types/eventGroupTypes";
import Switch from "@app/common/components/Switch";
import { CONFERENCING_PLATFORMS_BY_ID } from "@app/common/constants/platforms";
import { TOOLTIPS } from "@app/common/content";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import type { ConferencingMeetingPlatform } from "@app/common/types";
import { formatStartAndEndTime } from "@app/common/utils";

import RecurringMeetingBadge from "@rui/RecurringMeetingBadge";
import Stack, { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import UpcomingEventActionsCell from "./cells/UpcomingEventActionsCell";
import UpcomingEventRowHeader from "./UpcomingEventRowHeader";
import UpcomingEventShareDialog from "./UpcomingEventShareDialog";
import UpcomingEventsTableRowDenseContent from "./UpcomingEventsTableRowDenseContent";
import * as UpcomingEventUtils from "./utils";
import { PREDICTED_ROLES } from "../constants";
import * as Events from "../events";

export const getCannotBeFlexibleReason = (
  event: UserEventGroup
): string | undefined => {
  switch (event.canBeFlexibleRestriction) {
    case "not_meeting_owner":
      return "You cannot change flexibility because you are not the host";
    case "not_internal_meeting":
      return "This meeting cannot be marked flexible because it includes guests whose calendars can't be read";
    default:
      return undefined;
  }
};

export interface UpcomingEventsTableRowProps {
  event: UserEventGroup;
  onToggleInvite: (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
  onToggleFlexible: (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
}

const INITIAL_DIALOG_STATE: {
  open: boolean;
  tab: UpcomingEventShareDialogTab;
} = {
  open: false,
  tab: "sharing",
};

const UpcomingEventsTableRow = ({
  event,
  onToggleInvite,
  onToggleFlexible,
}: UpcomingEventsTableRowProps) => {
  const [shareDialog, setShareDialog] = useState(INITIAL_DIALOG_STATE);
  const isReadInvited = UpcomingEventUtils.isReadInvited(event);
  const { inMobileView } = useResponsiveView();

  const canBeFlexible = isNull(event.canBeFlexibleRestriction);
  const userCanInviteRead = UpcomingEventUtils.canInviteRead(event);
  const platformLogo =
    CONFERENCING_PLATFORMS_BY_ID[
      event.meetingPlatform as ConferencingMeetingPlatform
    ]?.icon;

  const canShare = UpcomingEventUtils.canShareEvent(event);
  const canInviteReadToManageSharing = !canShare && userCanInviteRead;

  const predictedRoleConfig =
    PREDICTED_ROLES[event.predictedAccessLevel ?? "no_role"];

  const cannotInviteReadTooltip = event.isPrivate
    ? TOOLTIPS.calendar.cannotInviteRead.eventIsPrivate
    : TOOLTIPS.calendar.cannotInviteRead.noVc;

  const openShareDialog = (tab: UpcomingEventShareDialogTab) => {
    Events.openShareDialog({ eventId: event.id });
    setShareDialog({ open: true, tab });
  };
  return (
    <>
      <TableRow>
        <TableCell sx={{ maxWidth: "250px" }}>
          <UpcomingEventRowHeader
            event={event}
            platformLogo={platformLogo}
            content={
              inMobileView ? (
                <UpcomingEventsTableRowDenseContent
                  event={event}
                  userCanInviteRead={userCanInviteRead}
                  isReadInvited={isReadInvited}
                  canBeFlexible={canBeFlexible}
                  cannotInviteReadTooltip={cannotInviteReadTooltip}
                  onToggleFlexible={onToggleFlexible}
                  onToggleInvite={onToggleInvite}
                />
              ) : undefined
            }
          />
        </TableCell>
        <DesktopOnlyTableCell>
          <Stack>
            <Row variant="left" spacing={1}>
              <Typography variant="bodySecondaryReg" color="shades.neutral.700">
                {event.startTime.weekdayShort}{" "}
                {event.startTime.toFormat("M/d/yy")}
              </Typography>
              {event.recurringEventId && <RecurringMeetingBadge size="12px" />}
            </Row>
            <Typography variant="labelReg" color="shades.neutral.500">
              {formatStartAndEndTime(event.startTime, event.endTime)}
            </Typography>
          </Stack>
        </DesktopOnlyTableCell>
        <DesktopOnlyTableCell align="center" sx={{ width: "170px" }}>
          <TooltipWrapper
            title={getCannotBeFlexibleReason(event)}
            show={!canBeFlexible}
          >
            <Switch
              checked={event.isFlexible}
              onChange={onToggleFlexible}
              disabled={!canBeFlexible}
            />
          </TooltipWrapper>
        </DesktopOnlyTableCell>
        <DesktopOnlyTableCell sx={{ width: "170px" }}>
          <Row variant="left" spacing={1}>
            <Typography variant="bodySecondaryReg" color="shades.neutral.500">
              {predictedRoleConfig.label}
            </Typography>
            {predictedRoleConfig.Icon &&
              predictedRoleConfig.showIcon(event) && (
                <predictedRoleConfig.Icon />
              )}
          </Row>
        </DesktopOnlyTableCell>
        <DesktopOnlyTableCell align="center" sx={{ width: "160px" }}>
          <TooltipWrapper
            show={!userCanInviteRead}
            title={cannotInviteReadTooltip}
          >
            <Switch
              checked={userCanInviteRead && isReadInvited}
              onChange={onToggleInvite}
              disabled={!userCanInviteRead}
            />
          </TooltipWrapper>
        </DesktopOnlyTableCell>
        <UpcomingEventActionsCell
          canInviteReadToManageSharing={canInviteReadToManageSharing}
          canShare={canShare}
          handleShareClick={() => openShareDialog("sharing")}
          handleAccessClick={() => openShareDialog("distribution")}
        />
      </TableRow>

      {canShare && shareDialog.open && (
        <UpcomingEventShareDialog
          event={event}
          open={shareDialog.open}
          initialTab={shareDialog.tab}
          onClose={() => setShareDialog(INITIAL_DIALOG_STATE)}
        />
      )}
    </>
  );
};

const UpcomingUserEventGroupsTableRowMemo = React.memo(
  UpcomingEventsTableRow,
  (prevProps, nextProps) => isEqual(prevProps.event, nextProps.event)
);

export default UpcomingUserEventGroupsTableRowMemo;
